<template>
  <div class="funding-report">
    <div class="bg-white  ">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">Fonlama Raporu</h1>
      
      <!-- Filters Section -->
      <div class="filters-container mb-6">
        <!-- Date Range Row -->
        <div class="flex gap-4 items-end mb-4">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Başlangıç Tarihi
            </label>
            <input
              v-model="startDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Bitiş Tarihi
            </label>
            <input
              v-model="endDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Yükleniyor...</span>
              <span v-else>Raporu Getir</span>
            </button>
          </div>
        </div>
        
        <!-- Retention Filters Row -->
        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Retention
            </label>
            <select
              v-model="selectedRetention"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tümü</option>
              <option v-for="retention in retentionList" :key="retention.id" :value="retention.id">
                {{ retention.username }}
              </option>
            </select>
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Retention Manager
            </label>
            <select
              v-model="selectedRetentionMd"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tümü</option>
              <option v-for="retentionMd in retentionMdList" :key="retentionMd.id" :value="retentionMd.id">
                {{ retentionMd.username }}
              </option>
            </select>
          </div>
          <div>
            <button
              @click="clearCache"
              class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cache Temizle
            </button>
          </div>
        </div>
      </div>

      <!-- Report Content with Pagination -->
      <div v-if="reportData && !loading" class="report-content">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-800">
            Rapor Dönemi: {{ reportData.period }}
          </h2>
        </div>

        <!-- Pagination Controls -->
        <div class="flex justify-between items-center mb-4">
          <div class="text-sm text-gray-700">
            Toplam {{ totalRows }} kayıt bulundu. 
            {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalRows) }} arası gösteriliyor.
          </div>
          <div class="flex gap-2">
            <button
              @click="previousPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Önceki
            </button>
            <span class="px-3 py-1">
              Sayfa {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sonraki
            </button>
          </div>
        </div>

        <!-- Monthly Funding Data -->
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Ay
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Müşteri Adı
                </th>
                   <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Retention
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Retention Manager
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Fonlama Bilgileri
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  İlk Fonlamadan Geçen Süre
                </th>
             
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <template v-for="(monthData, month) in paginatedData">
                <tr 
                  v-for="(customerFundings, customerId) in monthData"
                  :key="`${month}-${customerId}`"
                  class="hover:bg-gray-50"
                >
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                    <div class="font-medium">{{ formatMonth(month) }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                    <div class="font-medium text-blue-600">
                      {{ reportData.customers[customerId].name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      İlk Fonlama: {{ formatDate(reportData.customers[customerId].first_funding_date) }}
                    </div>
                  </td>
                   <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                    <div class="text-blue-600">
                      {{ reportData.customers[customerId].retention_name || '-' }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div class="text-purple-600">
                      {{ reportData.customers[customerId].retention_manager_name || '-' }}
                    </div>
                  </td>

                  <td class="px-6 py-4 text-sm text-gray-900 border-r">
                    <div class="space-y-1">
                      <div 
                        v-for="funding in customerFundings" 
                        :key="funding.sequence"
                        class="flex items-center space-x-2"
                      >
                        <span 
                          class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium"
                          :class="getFundingBadgeClass(funding.sequence)"
                        >
                          {{ funding.sequence }}
                        </span>
                        <span class="text-gray-600">
                          {{ formatDate(funding.funding_date) }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    <div class="space-y-1">
                      <div 
                        v-for="funding in customerFundings" 
                        :key="funding.sequence"
                        class="text-gray-600"
                      >
                        {{ funding.days_since_first }} gün
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Summary Section -->
        <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-blue-50 rounded-lg p-4">
            <div class="text-sm font-medium text-blue-600 mb-1">Toplam Müşteri</div>
            <div class="text-2xl font-bold text-blue-900">{{ getTotalCustomers() }}</div>
          </div>
          <div class="bg-green-50 rounded-lg p-4">
            <div class="text-sm font-medium text-green-600 mb-1">Toplam Fonlama</div>
            <div class="text-2xl font-bold text-green-900">{{ getTotalFundings() }}</div>
          </div>
          <div class="bg-purple-50 rounded-lg p-4">
            <div class="text-sm font-medium text-purple-600 mb-1">Çoklu Fonlama Yapan</div>
            <div class="text-2xl font-bold text-purple-900">{{ getMultipleFundingCustomers() }}</div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Rapor yükleniyor...</span>
      </div>

      <!-- Empty State -->
      <div v-if="!reportData && !loading && hasSearched" class="text-center py-12">
        <div class="text-gray-500 text-lg mb-2">Veri bulunamadı</div>
        <div class="text-gray-400">Seçili tarih aralığında fonlama verisi bulunmamaktadır.</div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'FundingReport',
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    
    return {
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      reportData: null,
      loading: false,
      hasSearched: false,
      selectedRetention: '',
      selectedRetentionMd: '',
      retentionList: [],
      retentionMdList: [],
      currentPage: 1,
      pageSize: 500,
      cache: {},
      cacheExpiry: 5 * 60 * 1000 // 5 dakika
    }
  },
  mounted() {
    this.loadRetentionUsers()
  },
  computed: {
    filteredMonthlyData() {
      if (!this.reportData || !this.reportData.data) return {}
      
      let filteredData = {}
      
      // Filtreleme işlemi
      Object.keys(this.reportData.data).forEach(month => {
        const monthData = this.reportData.data[month]
        const filteredMonth = {}
        
        Object.keys(monthData).forEach(customerId => {
          const customer = this.reportData.customers[customerId]
          
          // Retention filtresi
          if (this.selectedRetention && customer.retention_id != this.selectedRetention) {
            return
          }
          
          // Retention MD filtresi
          if (this.selectedRetentionMd && customer.retention_md_id != this.selectedRetentionMd) {
            return
          }
          
          filteredMonth[customerId] = monthData[customerId]
        })
        
        if (Object.keys(filteredMonth).length > 0) {
          filteredData[month] = filteredMonth
        }
      })
      
      return filteredData
    },
    
    sortedMonthlyData() {
      const data = this.filteredMonthlyData
      
      // Ay anahtarlarını ters sırala (en yeni en üstte)
      const sortedKeys = Object.keys(data).sort((a, b) => {
        return b.localeCompare(a)
      })
      
      // Sıralı object oluştur
      const sortedData = {}
      sortedKeys.forEach(key => {
        sortedData[key] = data[key]
      })
      
      return sortedData
    },
    
    totalRows() {
      let count = 0
      Object.values(this.sortedMonthlyData).forEach(monthData => {
        count += Object.keys(monthData).length
      })
      return count
    },
    
    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize)
    },
    
    paginatedData() {
      const allRows = []
      
      // Tüm veriyi düz liste haline getir
      Object.keys(this.sortedMonthlyData).forEach(month => {
        const monthData = this.sortedMonthlyData[month]
        Object.keys(monthData).forEach(customerId => {
          allRows.push({ month, customerId, data: monthData[customerId] })
        })
      })
      
      // Sayfalama uygula
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      const pageRows = allRows.slice(start, end)
      
      // Tekrar ay bazında grupla
      const result = {}
      pageRows.forEach(row => {
        if (!result[row.month]) {
          result[row.month] = {}
        }
        result[row.month][row.customerId] = row.data
      })
      
      return result
    }
  },
  methods: {
    async loadRetentionUsers() {
      try {
        // HTTP service kullanarak API çağrısı yapalım
        const usersResponse = await http.getUsers()
        if (usersResponse.data && usersResponse.data.users) {
          this.retentionList = usersResponse.data.users.filter(u => u.status === 1)
          this.retentionMdList = usersResponse.data.users.filter(u => u.status === 1)
        }
      } catch (error) {
        console.error('Kullanıcılar yüklenemedi:', error)
        // Fallback olarak boş liste
        this.retentionList = []
        this.retentionMdList = []
      }
    },
    
    getCacheKey() {
      return `funding_${this.startDate}_${this.endDate}_${this.selectedRetention}_${this.selectedRetentionMd}`
    },
    
    getFromCache(key) {
      const cached = this.cache[key]
      if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {
        return cached.data
      }
      return null
    },
    
    saveToCache(key, data) {
      this.cache[key] = {
        data: data,
        timestamp: Date.now()
      }
    },
    
    clearCache() {
      this.cache = {}
      alert('Cache temizlendi')
    },
    
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    
    async loadReport() {
      if (!this.startDate || !this.endDate) {
        alert('Lütfen başlangıç ve bitiş tarihlerini seçin')
        return
      }

      this.loading = true
      this.hasSearched = true
      this.currentPage = 1 // Reset sayfa numarası
      
      // Cache kontrolü
      const cacheKey = this.getCacheKey()
      const cachedData = this.getFromCache(cacheKey)
      
      if (cachedData) {
        this.reportData = cachedData
        this.loading = false
        return
      }

      try {
        const response = await http.getFundingReport({
          start_date: this.startDate,
          end_date: this.endDate,
          retention_id: this.selectedRetention || null,
          retention_md_id: this.selectedRetentionMd || null
        })

        if (response.data.success) {
          this.reportData = response.data
          // Cache'e kaydet
          this.saveToCache(cacheKey, response.data)
        } else {
          alert(response.data.message || 'Bir hata oluştu')
        }
      } catch (error) {
        console.error('Report loading error:', error)
        alert('Rapor yüklenirken bir hata oluştu')
      } finally {
        this.loading = false
      }
    },

    formatMonth(month) {
      const [year, monthNum] = month.split('-')
      const monthNames = [
        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
      ]
      return `${monthNames[parseInt(monthNum) - 1]} ${year}`
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    getFundingBadgeClass(sequence) {
      const colors = {
        1: 'bg-blue-100 text-blue-800',
        2: 'bg-green-100 text-green-800',
        3: 'bg-yellow-100 text-yellow-800',
        4: 'bg-purple-100 text-purple-800',
        5: 'bg-red-100 text-red-800'
      }
      return colors[sequence] || 'bg-gray-100 text-gray-800'
    },

    getTotalCustomers() {
      if (!this.filteredMonthlyData) return 0
      const customerIds = new Set()
      Object.values(this.filteredMonthlyData).forEach(monthData => {
        Object.keys(monthData).forEach(customerId => {
          customerIds.add(customerId)
        })
      })
      return customerIds.size
    },

    getTotalFundings() {
      if (!this.filteredMonthlyData) return 0
      let total = 0
      Object.values(this.filteredMonthlyData).forEach(monthData => {
        Object.values(monthData).forEach(customerFundings => {
          total += customerFundings.length
        })
      })
      return total
    },

    getMultipleFundingCustomers() {
      if (!this.filteredMonthlyData) return 0
      const customerFundingCounts = {}
      
      Object.values(this.filteredMonthlyData).forEach(monthData => {
        Object.entries(monthData).forEach(([customerId, customerFundings]) => {
          if (!customerFundingCounts[customerId]) {
            customerFundingCounts[customerId] = 0
          }
          customerFundingCounts[customerId] += customerFundings.length
        })
      })

      return Object.values(customerFundingCounts).filter(count => count > 1).length
    }
  }
}
</script>

<style scoped>
.funding-report {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8;
}

.filters-container {
  @apply bg-gray-50 rounded-lg p-4;
}

.report-content {
  @apply mt-6;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>